<!-- Slide for displaying a list of products that are not packages, urns, or caskets -->
<template>
	<div class="grid h-full grid-cols-[2fr_1fr] gap-12 px-10 pt-14">
		<div
			class="mt-8 overflow-y-auto"
			ref="scrollContainer"
		>
			<div class="sticky top-0 w-full bg-white p-1">
				<div class="relative flex w-1/4">
					<TagsDropdown
						:product-tags="productTags"
						@select="handleTagOption"
					/>
					<SortByDropdown @sort-by="handleSortByOption" />
				</div>
			</div>
			<button
				v-if="
					authStore.isEverplansAuthorized &&
					hasEssentialsPackages &&
					hasNonEssentialPackageInCart
				"
				type="button"
				@click="showOverlay = 'essentialsBundles'"
				class="hover:text-accent-400 text-secondary-400 mb-4 flex items-center gap-1"
			>
				<ChevronLeftIcon class="size-5 shrink-0" />
				{{ t('essentialsSlide.backToBundles') }}
			</button>
			<div
				class="grid grid-cols-1 gap-6 p-1 pb-14 md:grid-cols-2 xl:grid-cols-3"
			>
				<div
					v-for="p in merchandiseProducts"
					:key="p.id"
					class="cursor-pointer overflow-hidden rounded-sm p-4 shadow-md"
					:class="
						selectedProduct?.id === p.id ? 'bg-background-50' : 'bg-white'
					"
					@click="selectedProduct = p"
				>
					<div class="aspect-296/208">
						<BlobImage
							class="h-full w-full rounded-sm object-cover"
							:src="p.productImageUrl ?? undefined"
							:alt="p.name"
						/>
					</div>

					<h3
						class="line-clamp-2 pt-4 text-lg font-semibold"
						:class="
							selectedProduct?.id === p.id
								? 'text-primary-700!'
								: 'text-secondary-900'
						"
					>
						{{ p.name }}
					</h3>

					<div class="mt-2 flex gap-2">
						<PlanAvatar
							v-for="product in productPlans[p.id]"
							:key="product.plan.id"
							class="cursor-pointer"
							:index="product.index"
							:plan="product.plan"
							filled
							@click.stop="deleteProductForPlan(p, product.index)"
						/>
					</div>
				</div>
			</div>
		</div>

		<div class="overflow-y-auto py-5 pt-10 pb-20">
			<FormProduct
				:product="selectedProduct"
				:key="selectedProduct?.id"
				hasCounter
			/>
		</div>

		<button
			v-if="blok?.advanceButtonText"
			type="button"
			@click="onAdvanceButtonClick"
			data-test-id="advance-button"
			class="bg-accent-400 text-accent-700 hover:bg-accent-700 absolute right-8 bottom-8 flex flex-row items-center gap-2 rounded-lg px-10 py-2 font-sans text-base leading-loose font-bold hover:text-white"
		>
			{{ blok?.advanceButtonText }} <ArrowLongRightIcon class="size-6" />
		</button>
		<Transition
			:enter-active-class="hasMounted ? 'transition duration-300 ease-out' : ''"
			:enter-from-class="hasMounted ? 'translate-x-full opacity-0' : ''"
			:enter-to-class="hasMounted ? 'translate-x-0 opacity-100' : ''"
			:leave-active-class="
				leaveWithSlide ? 'transition duration-300 ease-in' : ''
			"
			:leave-from-class="leaveWithSlide ? 'translate-x-0 opacity-100' : ''"
			:leave-to-class="leaveWithSlide ? 'translate-x-full opacity-0' : ''"
		>
			<div
				class="pointer-events-auto fixed top-0 right-0 bottom-0 left-0 z-20 flex justify-center bg-white px-4 pt-24"
				v-if="
					authStore.isEverplansAuthorized && showOverlay === 'essentialsBundles'
				"
			>
				<OverlayEssentialsBundles
					class="w-full"
					:blok="blok"
					:heading-large-html="headingLargeHtml"
					@close-with-slide="handleOverlayClose(true)"
				/>
			</div>
		</Transition>
	</div>
</template>

<script setup lang="ts">
import BlobImage from '../common/BlobImage.vue'
import FormProduct from '../merchandise/FormProduct.vue'
import PlanAvatar from '../presentation/PlanAvatar.vue'
import OverlayEssentialsBundles from './OverlayEssentialsBundles.vue'
import type { SfTag } from '@/apiTypes'
import type { IndexedPlan } from '@/classes/Plan'
import { type PbLineItemProductAndPricingOptions } from '@/classes/PriceBook'
import SortByDropdown from '@/components/presentation/SortByDropdown.vue'
import TagsDropdown from '@/components/presentation/TagsDropdown.vue'
import {
	buildProductTagOptions,
	matchesSelectedTag,
	type SortByOption,
	sortProductListItems,
} from '@/components/presentation/productListControls'
import { PbProductType } from '@/enums/PbProductType'
import { PbSubcategory } from '@/enums/PbSubcategory'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import type { EssentialSlideStoryblok } from '@/sbTypes'
import { useAuthStore } from '@/stores/auth'
import { usePresentationController } from '@/stores/presentationController'
import { useSessionStore } from '@/stores/session'
import { ArrowLongRightIcon, ChevronLeftIcon } from '@heroicons/vue/24/solid'
import { renderRichText } from '@storyblok/vue'
import { Swiper } from 'swiper'
import { computed, type PropType, ref, watch } from 'vue'

const authStore = useAuthStore()

const { t } = usePresentationLocale()

const showOverlay = ref<'none' | 'essentialsBundles'>('none')

const leaveWithSlide = ref(false)

const handleOverlayClose = (_slide: true) => {
	leaveWithSlide.value = true
	showOverlay.value = 'none'
}

const props = defineProps({
	blok: Object as PropType<EssentialSlideStoryblok>,
	parentSwiper: Swiper,
	active: Boolean,
})

const headingLargeHtml = computed(
	() => renderRichText(props.blok?.headingLarge) ?? ''
)

const sessionStore = useSessionStore()
const controller = usePresentationController()
const { priceBook, isLineItemInPlan, mockProductEverplans } = useMerchandise()

const scrollContainer = ref<HTMLElement | null>()
const selectedProduct = ref<PbLineItemProductAndPricingOptions | undefined>()
const selectedTag = ref<SfTag | null>(null)
const selectedSortBy = ref<SortByOption['value']>('alphabetical')
const plans = computed(() => {
	return sessionStore.activePlans
})
const productTags = computed<SfTag[]>(() => {
	const merchandiseIds = new Set(
		allMerchandiseProducts.value.map((product) => product.id)
	)

	return buildProductTagOptions(
		priceBook.value?.getProductTags((product) =>
			merchandiseIds.has(product.id)
		) ?? [],
		t('general.all')
	)
})

const handleSortByOption = (option: SortByOption) => {
	selectedSortBy.value = option.value
}

const handleTagOption = (option: SfTag) => {
	selectedTag.value = option
}

/** Delete a product in a plan */
const deleteProductForPlan = (
	product: PbLineItemProductAndPricingOptions,
	index: number
) => {
	const plan = sessionStore.activePlans[index]
	if (!plan) return

	// Remove Everplans
	if (product.id === 'everplans') {
		plan.everplansIncluded = false
		return
	}

	// Find either a top-level cart item or a sub-item
	const existing =
		plan.cartItems.find((ci) => ci.lineItemId === product.id) ??
		plan.cartItems
			.flatMap((ci) => ci.subItems)
			.find((sub) => sub.lineItemId === product.id)

	// If it exists, delete it
	if (existing) {
		plan.delete(existing)
	}
}

const hasNonEssentialPackageInCart = computed(() => {
	if (!priceBook.value) return false
	return (sessionStore.activePlans ?? []).some((plan) =>
		plan.cartItems.some((ci) => {
			const li = priceBook.value!.findLineItem(ci.lineItemId)
			return (
				li?.category === PbProductType.Package &&
				li?.subcategory !== PbSubcategory.PackageEssentials
			)
		})
	)
})

watch(hasNonEssentialPackageInCart, (hasAny) => {
	if (!hasAny && showOverlay.value === 'essentialsBundles') {
		showOverlay.value = 'none'
	}
})

/**
 * Hide the Everplans tile when every packaged plan already gets Everplans
 * from an isNoCostEverplans package. A plan without that flag still needs the tile.
 */
const suppressEverplansTile = computed(() => {
	if (!priceBook.value) {
		return false
	}

	const plansWithPackages = (sessionStore.activePlans ?? []).filter((plan) =>
		plan.cartItems.some((cartItem) => {
			const lineItem = priceBook.value?.findLineItem(cartItem.lineItemId)

			return lineItem?.category === PbProductType.Package
		})
	)

	if (plansWithPackages.length === 0) {
		return false
	}

	return plansWithPackages.every((plan) =>
		plan.cartItems.some((cartItem) => {
			const lineItem = priceBook.value?.findLineItem(cartItem.lineItemId)

			return !!lineItem?.isNoCostEverplans
		})
	)
})

/** Base list of merchandise + cash advance products (cash advance first on dedupe). */
const allMerchandiseProducts = computed(() => {
	if (!priceBook.value) return []

	const mockCashAdvanceItems: PbLineItemProductAndPricingOptions[] = []
	if (
		authStore.isEverplansAuthorized &&
		mockProductEverplans.value &&
		!suppressEverplansTile.value
	) {
		mockCashAdvanceItems.push(mockProductEverplans.value)
	}

	const merch = priceBook.value.getMiscMerchandise() ?? []
	const cashAdvance =
		priceBook.value.getCashAdvanceProducts(mockCashAdvanceItems) ?? []
	const miscNonOther = merch.filter(
		(product) => product.subcategory !== PbSubcategory.Other
	)
	const miscOther = merch.filter(
		(product) => product.subcategory === PbSubcategory.Other
	)

	const products = [...cashAdvance, ...miscNonOther, ...miscOther]

	return products.filter(
		(product, index) =>
			products.findIndex((item) => item.id === product.id) === index
	)
})

/** Visible list after filtering and sorting */
const merchandiseProducts = computed(() => {
	const products = allMerchandiseProducts.value.filter((product) =>
		matchesSelectedTag(product, selectedTag.value)
	)

	return sortProductListItems(products, selectedSortBy.value)
})

/** Dictionary for each product of all the plans and whether the product is in their cart */
const productPlans = computed<Record<string, IndexedPlan[]>>(() => {
	if (!plans.value) return {}
	if (!merchandiseProducts.value) return {}

	return merchandiseProducts.value.reduce<Record<string, IndexedPlan[]>>(
		(acc, product) => {
			const isEverplans =
				!!mockProductEverplans?.value &&
				product.id === mockProductEverplans.value.id

			return {
				...acc,
				[product.id]: plans
					.value!.map((plan, index): IndexedPlan => ({ index, plan }))
					.filter((indexedPlan) => {
						if (isEverplans) {
							return indexedPlan.plan.everplansIncluded === true
						}
						return isLineItemInPlan(product, indexedPlan.plan)
					}),
			}
		},
		{}
	)
})

/** This watch is needed to ensure that the selected product is still visible after filtering. */
watch(
	merchandiseProducts,
	(products) => {
		if (!products.length) {
			selectedProduct.value = undefined
			return
		}

		const selectedStillVisible = products.some(
			(product) => product.id === selectedProduct.value?.id
		)

		if (!selectedStillVisible) {
			selectedProduct.value = products[0]
		}
	},
	{ immediate: true }
)

const hasMounted = ref(false)

const hasEssentialsPackages = computed(() => {
	return (
		priceBook.value?.packages?.some(
			(p) => p.subcategory === PbSubcategory.PackageEssentials
		) ?? false
	)
})

/**
 * Scroll to top and show the Essentials bundles overlay once per presentation
 * Note: Overlay is reset when the presentation is exited
 */
watch(
	() => props.active,
	(isActive) => {
		if (isActive) {
			scrollContainer.value?.scrollTo(0, 0)
			if (
				hasEssentialsPackages.value &&
				hasNonEssentialPackageInCart.value &&
				!controller.hasShownEssentialsBundlesOverlay
			) {
				showOverlay.value = 'essentialsBundles'
				controller.markEssentialsBundlesOverlayShown()
			}

			// Prevent enter transition on first load
			if (!hasMounted.value) hasMounted.value = true
		}
	},
	{ immediate: true }
)

/** Find the review slide and go to it */
function onAdvanceButtonClick() {
	const essentialsSlide = controller.allSlides?.find(
		(slide) => slide.component === 'review-slide'
	)
	if (!essentialsSlide) {
		throw new Error('Could not find review slide')
	}
	controller.gotoSlideId(essentialsSlide?._uid)
}

defineExpose({ merchandiseProduct: merchandiseProducts })
</script>
